export const get_mapped_value = (map, key, fallback = '') => {
  if (map.has(key)) {
    return map.get(key);
  }

  return fallback;
};

export const show_onboarding_system_loader = (message = 'Loading...') => {
  window.dispatchEvent(new CustomEvent('system-loader:show', {
    detail: message
  }));
};

export const hide_onboarding_system_loader_after_render = (instance) => {
  if (!instance?.$nextTick) {
    window.dispatchEvent(new CustomEvent('system-loader:hide'));
    return;
  }

  instance.$nextTick(() => {
    window.dispatchEvent(new CustomEvent('system-loader:hide'));
  });
};

const first_value = (values, fallback = '') => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  return value === undefined ? fallback : value;
};

const normalize_text = (value) => String(value || '').trim().toLowerCase();

export const JOURNEY_COLORS = new Map([
  ['supplier', '#416f6e'],
  ['employee', '#417d71'],
  ['merchant', '#be8721'],
  ['partner', '#ba426a'],
  ['intern', '#6642cc'],
  ['client', '#597c2c']
]);

export const PROGRESS_COLORS = new Map([
  ['warning', '#bf4b08'],
  ['success', '#417d71'],
  ['navy', '#416f6e']
]);

export const RISK_BADGE_CLASSES = new Map([
  ['low', 'bg-success-subtle text-success-emphasis'],
  ['medium', 'bg-warning-subtle text-warning-emphasis'],
  ['high', 'bg-danger-subtle text-danger-emphasis'],
  ['pending', 'bg-secondary-subtle text-secondary-emphasis']
]);

export const ACTIVATION_BADGE_CLASSES = new Map([
  ['ready', 'bg-success-subtle text-success-emphasis'],
  ['pending', 'bg-warning-subtle text-warning-emphasis'],
  ['progress', 'bg-warning-subtle text-warning-emphasis'],
  ['blocked', 'bg-danger-subtle text-danger-emphasis']
]);

export const ANSWER_DOT_CLASSES = new Map([
  ['danger', 'bg-danger'],
  ['success', 'bg-success'],
  ['warning', 'bg-warning'],
  ['secondary', 'bg-secondary']
]);

export const JOURNEY_DOT_CLASSES = new Map([
  ['supplier', 'text-bg-primary'],
  ['employee', 'text-bg-success'],
  ['merchant', 'text-bg-warning'],
  ['partner', 'text-bg-danger'],
  ['intern', 'text-bg-info'],
  ['client', 'text-bg-secondary']
]);

export const get_journey_dot_style = (tone) => ({
  backgroundColor: get_mapped_value(JOURNEY_COLORS, tone, '#f09040')
});

export const resolve_case_risk_label = (case_item = {}) => {
  const resolved_risk = first_value([
    case_item?.risk,
    case_item?.risk_rating,
    case_item?.risk_level,
    case_item?.risk_result?.risk_level,
    case_item?.risk_category,
    case_item?.risk_label,
    case_item?.onboarding_status?.risk_assessments?.[0]?.risk_level,
    case_item?.onboarding_status?.risk_assessments?.[0]?.result?.risk_level,
    case_item?.onboarding_status?.risk_assessments?.[0]?.latest_result?.risk_level
  ], '');
  const normalized_risk = normalize_text(resolved_risk);

  return ['high', 'medium', 'moderate', 'low'].some((level) => normalized_risk.includes(level))
    ? resolved_risk
    : 'Not assessed';
};

export const resolve_case_risk_tone = (case_item = {}) => {
  const normalized_risk = normalize_text(resolve_case_risk_label(case_item));
  if (normalized_risk.includes('high')) return 'high';
  if (normalized_risk.includes('medium') || normalized_risk.includes('moderate')) return 'medium';
  if (normalized_risk.includes('low')) return 'low';
  return 'pending';
};

export const resolve_case_risk_score = (case_item = {}) => {
  const numeric_score = Number(first_value([
    case_item?.risk_score,
    case_item?.risk_rating_score,
    case_item?.risk_result?.total_score,
    case_item?.threshold_assessment?.score,
    case_item?.threshold_assessment?.risk_score
  ], Number.NaN));

  if (Number.isFinite(numeric_score)) {
    return Math.max(0, Math.min(100, Math.round(numeric_score)));
  }

  const risk_tone = resolve_case_risk_tone(case_item);
  if (risk_tone === 'high') return 85;
  if (risk_tone === 'medium') return 58;
  if (risk_tone === 'pending') return 0;
  return 22;
};

const SLA_STATUS_LABEL_RULES = [
  { matches: (status) => status.includes('breach') || status.includes('overdue'), label: 'Breached' },
  { matches: (status) => status.includes('risk'), label: 'At risk' },
  { matches: (status) => status.includes('track') || status.includes('within'), label: 'On track' }
];

const resolve_explicit_sla_label = (explicit_status) =>
  SLA_STATUS_LABEL_RULES.find((rule) => rule.matches(explicit_status))?.label || '';

const resolve_sla_date_label = (raw_date) => {
  if (!raw_date || raw_date === '-') return 'Unscheduled';

  const parsed_date = new Date(raw_date);
  if (Number.isNaN(parsed_date.getTime())) return String(raw_date);

  const diff_days = (parsed_date.getTime() - Date.now()) / (24 * 60 * 60 * 1000);
  if (diff_days < 0) return 'Breached';
  return diff_days <= 2 ? 'At risk' : 'On track';
};

export const resolve_case_sla_label = (case_item = {}) => {
  const explicit_status = normalize_text(first_value([
    case_item?.sla_status,
    case_item?.sla_label
  ], ''));
  const explicit_label = resolve_explicit_sla_label(explicit_status);

  if (explicit_label) return explicit_label;

  const raw_date = first_value([
    case_item?.onboarding_activate_by,
    case_item?.eta,
    case_item?.sla
  ], '');

  return resolve_sla_date_label(raw_date);
};

export const resolve_case_sla_tone = (case_item = {}) => {
  const label = normalize_text(resolve_case_sla_label(case_item));
  if (label.includes('breach') || label.includes('risk')) return 'warning';
  if (label.includes('track')) return 'success';
  return 'secondary';
};

export const resolve_template_sla_label = (template = {}) => first_value([
  template?.target_sla,
  template?.sla,
  template?.sla_label,
  template?.activate_by,
  template?.onboarding_activate_by,
  template?.turnaround_time,
  template?.turnaround_days,
  template?.due_in
], '-');
